import { Injectable } from '@angular/core';
import {
    SurveyUserTypes,
    Impression,
    JobMatchValue
} from '../constants-types/Enums';
import { ABCD } from '../core-data-model/BehavioralFactor.model';
import { Job } from '../core-data-model/Job.model';
import { Person } from '../core-data-model/Person.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';

@Injectable({ providedIn: 'root' })
export class CsvConversionService {
    constructor(
        private TR: TranslationService,
        private userProfile: UserProfileSettingsService
    ) {}

    // Note there is a theortical problem where you have columns that have
    // the same name. Ignoring this issue for now since we have few people
    // who use this feature and few in other languages

    addResultTypeColumn(row: any, result: SearchResult) {
        let val;
        if (result instanceof Person) {
            val = this.TR.key('Person');
        } else if (result instanceof Job) {
            val = this.TR.key('label.common-terms.job');
        } else {
            val = '';
        }
        row[this.TR.key('common.ResultType')] = val;
    }
    addResultNameColumn(row: any, result: SearchResult) {
        let val;
        if (result instanceof Person) {
            val = result.fullName(true);
        } else if (result instanceof Job) {
            val = result.title;
        } else {
            val = '';
        }
        row[this.TR.key('label.common-terms.name')] = val;
    }
    addEmailColumn(row: any, result: SearchResult) {
        row[this.TR.key('label.email')] =
            result instanceof Person ? result.email : '';
    }
    addResultJobTitleColumn(row: any, result: SearchResult) {
        row[this.TR.key('label.common-terms.job')] =
            result instanceof Person && result.job && result.job.title
                ? result.job.title
                : '';
    }
    addLastUpdatedColumn(row: any, result: SearchResult) {
        const date =
            result instanceof Person || result instanceof Job
                ? result.lastUpdated
                : null;
        row[this.TR.key('label.common-terms.last.modified')] = date
            ? date.toString()
            : '';
    }
    addPersonTypeColumn(row: any, result: SearchResult) {
        row[this.TR.key('label.common-terms.type')] =
            result instanceof Person
                ? this.TR.key(SurveyUserTypes[result.type])
                : '';
    }
    addBehavioralColumn(row: any, result: SearchResult) {
        if (result instanceof Person && result.hasBehavioralPattern()) {
            ABCD.forEach((letter) => {
                row[letter] = result.self[letter];
            });
        }
    }
    addReferenceProfileNameColumn(row: any, result: SearchResult) {
        row[this.TR.key('label.common-terms.ref-pattern')] =
            result instanceof Person && !!result.referencePattern
                ? this.TR.key(result.referencePattern.key)
                : '';
    }
    addCognitiveColumn(row: any, result: SearchResult) {
        if (this.userProfile.showCognitive) {
            row[this.TR.key('label.common-terms.cognitive')] =
                result instanceof Person && result.highestCognitiveAssessment
                    ? result.highestCognitiveAssessment.score.toString()
                    : '';
        }
    }

    addResultBehaviorScoreIdColumn(row: any, result: SearchResult) {
        row[this.TR.key('PatternCode')] =
            result instanceof Person && result.originalAssessment
                ? result.originalAssessment.scoreId
                : '';
    }
    addAdministeredByBehavioralColumn(row: any, result: SearchResult) {
        row[this.TR.key('table.column-administeredByBehavioral')] =
            result instanceof Person
                ? result.behavioralAssessmentAdministeredBy
                : '';
    }
    addAdministeredByCognitiveColumn(row: any, result: SearchResult) {
        row[this.TR.key('table.column-administeredByCognitive')] =
            result instanceof Person
                ? result.getCognitiveAdministeredByForDisplay()
                : '';
    }

    addMatchFavoriteColumn(row: any, result: SearchResult) {
        row[this.TR.key('label.common-terms.favorite')] =
            result instanceof Person
                ? Impression[result.jobFit.personImpression]
                : '';
    }

    addMatchOverallCompatibilityScoreColumn(
        row: any,
        result: SearchResult,
        job: Job
    ) {
        if (job.useCognitiveForMatch && this.userProfile.showCognitive) {
            row[this.TR.key('common.overall')] =
                result instanceof Person && result.jobFit
                    ? result.jobFit.overallFitScore &&
                      result.jobFit.overallFitScore.toString()
                    : null;
        }
    }

    addMatchBehavioralScoreColumn(row: any, result: SearchResult) {
        row[this.TR.key('common.BehavioralMatch')] =
            result instanceof Person && result.jobFit
                ? result.jobFit.behavioralFitScore &&
                  result.jobFit.behavioralFitScore.toString()
                : null;
    }
    addMatchCognitiveScoreColumn(row: any, result: SearchResult, job: Job) {
        if (job.useCognitiveForMatch && this.userProfile.showCognitive) {
            row[this.TR.key('common.CognitiveMatch')] =
                result instanceof Person && result.jobFit
                    ? result.jobFit.cognitiveFitScore &&
                      result.jobFit.cognitiveFitScore.toString()
                    : null;
        }
    }

    addLastSentColumn(row: any, result: SearchResult) {
        row[this.TR.key('label.common-terms.date.sent')] =
            result instanceof Person ? result.lastSent.toString() : '';
    }
    addLastCompletedColumn(row: any, result: SearchResult) {
        row[this.TR.key('DateCompleted')] =
            result instanceof Person ? result.lastCompleted.toString() : '';
    }
    addFolderColumn(row: any, result: SearchResult) {
        row[this.TR.key('label.common-terms.folder')] =
            result instanceof Person || result instanceof Job
                ? result.folder.folderPath
                : null;
    }
    addJobPipelineHealth(row: any, result: SearchResult) {
        let value: string;
        if (result instanceof Job) {
            value = `Total Candidates: ${result.jobPipeline.totalCandidates}. `;
            [
                JobMatchValue.Pending,
                JobMatchValue.Low,
                JobMatchValue.Ok,
                JobMatchValue.Great
            ].forEach((enumValue) => {
                value += `${JobMatchValue[enumValue]}: ${result.jobPipeline.healthCounts[enumValue]} `;
            });
        }
        row[this.TR.key('table-head.jobPipelineHealth')] = value.trim() || '';
    }
}
